import { consume } from "@lit/context";
import { html, LitElement, PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ProductSelectionContext, productSelectionContextKey } from "../product-selection.context";
import { AvailableGroupedProducts } from "../../types";
import { delay, of, switchMap } from "rxjs";
import { repeat } from "lit/directives/repeat.js";
import autoAnimate, { AnimationController } from "@formkit/auto-animate";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { classMap } from "lit/directives/class-map.js";

@customElement("product-picker")
export class ProductPickerElement extends LitElement {

    private rootElement = createRef<HTMLElement>();
    private animationController: AnimationController | null = null;

    @consume({ context: productSelectionContextKey})
    private productSelectionContext! : ProductSelectionContext;

    @state()
    private availableProducts: AvailableGroupedProducts[] | null = null;

    @state()
    private isLoadingProducts: boolean = false;

    override connectedCallback() {
        super.connectedCallback();
        const delayedProductLoading = this.productSelectionContext.isLoadingProducts$.pipe(
            switchMap(isLoading =>
                isLoading
                ? of(isLoading) // emit true immediately
                : of(isLoading).pipe(delay(1000)) // delay emitting false
        ));
        delayedProductLoading.subscribe({ next: (next) => this.isLoadingProducts = next });
        this.productSelectionContext.availableProducts$.subscribe({ next: (next) => this.availableProducts = next });
    }

    protected override firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        if(this.rootElement.value && !this.animationController) {
            this.animationController = autoAnimate(this.rootElement.value);
        }
    }

    protected override render() {
        return html`
            <div class="flex flex-col gap-2" ${ref(this.rootElement)}>
                ${when(this.isLoadingProducts,
                    () => this.renderLoadingSkeleton(),
                    () => repeat(
                        this.availableProducts ?? [],

                        productGroup => productGroup.groupId,
                        productGroup => {
                            const moreThanOneProduct = (productGroup.products && productGroup.products.length > 1) ?? false;

                            const productListClasses = {
                                "divide-y": moreThanOneProduct,
                                "divide-neutral-300": moreThanOneProduct,
                                "*:py-2": moreThanOneProduct,
                                "*:pt-2": moreThanOneProduct === false
                            };
                            return html`
                                <div class="bg-white rounded-lg p-4 flex flex-col">
                                    ${when(productGroup.groupName, () => html`
                                        <div class="border-b border-mcr-purple text-lg text-mcr-purple font-bold">${productGroup.groupName}</div>
                                    `)}
                                    <div class="flex flex-col ${classMap(productListClasses)}">
                                        ${repeat(productGroup.products ?? [], product => product.id, product => html`
                                            <product-item .product=${product}></product-item>
                                        `)}
                                    </div>
                                </div>
                            `;
                        }))}
                ${when(!this.isLoadingProducts && (!this.availableProducts || this.availableProducts.length === 0), () => html`
                    <div class="text-center rounded-lg text-gray-500 bg-gray-100 py-4">No products available for the selected date.</div>
                `)}
            </div>
        `;
    }

    protected override createRenderRoot() {
        return this;
    }


    private renderLoadingSkeleton() {
        return html`
            <div class="bg-white/10 rounded-lg p-4 flex flex-col">
                <div class="flex gap-2 py-2 animate-pulse">
                    <div class="w-2/5 h-12 bg-mcr-purple-400 lg:w-36 lg:h-20">&nbsp;</div>
                    <div class="flex flex-col grow gap-2">
                        <div class="w-3/5 bg-mcr-purple-400">&nbsp;</div>
                        <p class="w-4/5 bg-mcr-purple-400">&nbsp;</p>
                    </div>
                </div>
            </div>
            <div class="bg-white/10 rounded-lg p-4 flex flex-col">
                <div class="text-lg font-bold bg-mcr-purple-400 animate-pulse py-2">&nbsp;</div>
                <div class="flex gap-2 py-2 animate-pulse">
                    <div class="w-2/5 h-12 bg-mcr-purple-400 lg:w-36 lg:h-20">&nbsp;</div>
                    <div class="flex flex-col grow gap-2">
                        <div class="w-3/5 bg-mcr-purple-400">&nbsp;</div>
                        <p class="w-4/5 bg-mcr-purple-400">&nbsp;</p>
                    </div>
                </div>
                <div class="flex gap-2 py-2 animate-pulse">
                    <div class="w-2/5 h-12 bg-mcr-purple-400 lg:w-36 lg:h-20">&nbsp;</div>
                    <div class="flex flex-col grow gap-2">
                        <div class="w-3/5 bg-mcr-purple-400">&nbsp;</div>
                        <p class="w-4/5 bg-mcr-purple-400">&nbsp;</p>
                    </div>
                </div>
            </div>
        `;
    }
}